import mixpanel from 'mixpanel-browser'

const CONSENT_KEY = 'xp_cookie_consent_v2'

export type ConsentCategories = {
  analytics: boolean
  advertising: boolean
}

declare global {
  interface Window {
    gtag?: (...args: unknown[]) => void
    dataLayer?: unknown[]
  }
}

export const getStoredConsent = (): ConsentCategories | null => {
  if (typeof window === 'undefined') return null
  const raw = window.localStorage.getItem(CONSENT_KEY)
  if (!raw) return null
  try {
    const parsed = JSON.parse(raw)
    if (typeof parsed?.analytics === 'boolean' && typeof parsed?.advertising === 'boolean') {
      return parsed
    }
  } catch {
    // ignore
  }
  return null
}

const writeStoredConsent = (c: ConsentCategories) => {
  window.localStorage.setItem(CONSENT_KEY, JSON.stringify(c))
}

const applyGtagConsent = (c: ConsentCategories) => {
  if (typeof window === 'undefined' || typeof window.gtag !== 'function') return
  window.gtag('consent', 'update', {
    analytics_storage: c.analytics ? 'granted' : 'denied',
    ad_storage: c.advertising ? 'granted' : 'denied',
    ad_user_data: c.advertising ? 'granted' : 'denied',
    ad_personalization: c.advertising ? 'granted' : 'denied',
  })
}

let mixpanelInitialized = false
const initMixpanel = () => {
  if (mixpanelInitialized) return
  if (process.env.NODE_ENV !== 'production') return
  mixpanel.init('3017e8e712348e328668c312f836036b', {
    autocapture: true,
    record_sessions_percent: 100,
    persistence: 'cookie',
    ip: true,
    property_blacklist: [],
  })

  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone
    const language = navigator.language
    mixpanel.register({
      timezone: tz,
      locale: language,
      viewport_width: window.innerWidth,
      viewport_height: window.innerHeight,
      referrer: document.referrer || undefined,
    })

    const distinctId = mixpanel.get_distinct_id()
    mixpanel.identify(distinctId)
    mixpanel.people.set_once({
      $first_seen: new Date().toISOString(),
      first_referrer: document.referrer || undefined,
      first_landing_page: window.location.pathname,
    })
    mixpanel.people.set({
      timezone: tz,
      locale: language,
      last_seen_page: window.location.pathname,
    })
  } catch {
    // ignore — best-effort enrichment
  }

  mixpanelInitialized = true
}

export const isMixpanelReady = () => mixpanelInitialized

export const saveConsent = (c: ConsentCategories) => {
  writeStoredConsent(c)
  applyGtagConsent(c)
  if (c.analytics) initMixpanel()
}

export const acceptAllConsent = () => {
  saveConsent({ analytics: true, advertising: true })
}

export const applyStoredConsent = () => {
  const c = getStoredConsent()
  if (!c) return
  applyGtagConsent(c)
  if (c.analytics) initMixpanel()
}
